import startCase from 'lodash/startCase';

import {
  fetchingError,
} from '../../../shared/store/actions';
import { fetchActionFactory } from '../../../shared/store/fetchFactory';

import {
  FETCHING_DEFERRED_ROLLFORWARD_REPORT,
  FETCHING_DEFERRED_ROLLFORWARD_REPORT_SUCCESS,
  FETCHING_DEFERRED_ROLLFORWARD_REPORT_FAILURE,
  FETCHING_DEFERRED_ROLLFORWARD_REPORT_DATA_FOR_ALL_PILLS,
  FETCHING_DEFERRED_ROLLFORWARD_REPORT_DATA_FOR_ALL_PILLS_SUCCESS,
  FETCHING_DEFERRED_ROLLFORWARD_REPORT_DATA_FOR_ALL_PILLS_FAILURE,
  CHANGE_AGGREGATION_DROPLIST_VALUE,
  UPDATE_EDITED_CELL,
  REVERT_EDITED_CELLS,
  FETCH_OBLIGATIONS,
} from './types';

import {
  fetchReportDataWithTaxYears as fetchReportDataApi,
  fetchObligations as fetchObligationsApi,
} from '../../api';
import { errorNotification } from '../../../shared/store/notificationActions';
import downloadXlsxBSMReport from '../downloadXlsxBSMReport';
import downloadCsvBSMReport from '../downloadCsvBSMReport';
import { AllTabBookTypes, BookTypePillTypes } from '../../../shared/bookTypePills';
import { GROSS, NET } from '../../shared/taxEntry.constants';

export const fetchObligations = fetchActionFactory(
  FETCH_OBLIGATIONS,
  fetchObligationsApi,
  { field: 'obligations' }
);

export const fetchReportDataForViewDeferredRollforwardPage = ({
  periodFrom,
  periodTo,
  taxYear,
  slGroupType,
  slGroupItem,
  countryTaxType,
  bookType,
  bookTypes,
  reportingCurrency,
  aggregateBy,
  grossFlag,
  currentPeriod,
}) => async dispatch => {
  dispatch({ type: FETCHING_DEFERRED_ROLLFORWARD_REPORT });

  try {
    const allBookTypes = AllTabBookTypes
      .filter(type => bookTypes.includes(type));

    const response = await fetchReportDataApi({
      reportName: 'DeferredRollforward',
      fromPeriod: periodFrom.name,
      toPeriod: periodTo.name,
      taxYear: taxYear.value,
      groupType: slGroupType.value,
      groupItem: slGroupItem.value,
      countryTaxType: countryTaxType.value,
      bookType: bookType === 'ALL' ? allBookTypes : bookType,
      reportingCurrency,
      aggregateBy,
      grossFlag,
    });

    await dispatch(fetchObligations(currentPeriod));

    dispatch({
      type: FETCHING_DEFERRED_ROLLFORWARD_REPORT_SUCCESS,
      reportData: response,
    });
  } catch (error) {
    dispatch({ type: FETCHING_DEFERRED_ROLLFORWARD_REPORT_FAILURE });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const buildDeferredRollforwardExportPayload = async ({
  periodFrom,
  periodTo,
  taxYear,
  slGroupType,
  slGroupItem,
  countryTaxType,
  reportingCurrency,
  bookTypes,
  hideDecimals,
  shouldDisplayZero,
  negativeMinus,
  environmentSuffix,
  aggregateBy,
  grossFlag,
  columnDefs,
}) => {
  const filteredBookTypes = AllTabBookTypes
    .filter(type => bookTypes.includes(type));

  const filteredBookTypesList = [
    ...filteredBookTypes,
    ...(filteredBookTypes.length && bookTypes.includes('A') ? ['ALL'] : []),
  ];

  const reportData = await Promise.all(
    filteredBookTypesList
      .map(bookType => fetchReportDataApi({
        reportName: 'DeferredRollforwardForExport',
        fromPeriod: periodFrom.name,
        toPeriod: periodTo.name,
        taxYear: taxYear.value,
        groupType: slGroupType.value,
        groupItem: slGroupItem.value,
        countryTaxType: countryTaxType.value,
        bookType: bookType === 'ALL' ? filteredBookTypes : bookType,
        reportingCurrency,
        aggregateBy,
        grossFlag,
      }))
  );

  const reports = filteredBookTypesList.reduce((accu, bookType, index) => ({
    ...accu,
    [bookType]: {
      ...reportData[index],
      columnDefs,
    },
  }), {});

  const fileName = `TLS_Deferred_Rollforward_${periodFrom.value}_${periodTo.value}_${taxYear.label}_${countryTaxType.value}_${slGroupItem.value}_${aggregateBy}${environmentSuffix}`;
  const description = `From ${periodFrom.name} to ${periodTo.name}, Country Tax Type: ${countryTaxType.value}, ${slGroupType.label}: ${slGroupItem.label}, Currency: ${reportingCurrency}, View by: ${startCase(aggregateBy)}, ${grossFlag ? GROSS : NET}`;

  return {
    reportTitle: 'Deferred Rollforward',
    reportData: {
      GAAP: reports[BookTypePillTypes.GAAP],
      GAAP_TO_STAT: reports[BookTypePillTypes.GAAP_TO_STAT],
      STAT: reports[BookTypePillTypes.STAT],
      ALL: reports.ALL,
    },
    description,
    fileName,
    hideDecimals,
    shouldDisplayZero,
    negativeMinus,
    descriptionColumnIndex: -1,
  };
};

const runDeferredRollforwardExport = download => params => async dispatch => {
  dispatch({
    type: FETCHING_DEFERRED_ROLLFORWARD_REPORT_DATA_FOR_ALL_PILLS,
  });

  try {
    const payload = await buildDeferredRollforwardExportPayload(params);
    await download(payload);

    dispatch({
      type: FETCHING_DEFERRED_ROLLFORWARD_REPORT_DATA_FOR_ALL_PILLS_SUCCESS,
    });
  } catch (error) {
    dispatch({
      type: FETCHING_DEFERRED_ROLLFORWARD_REPORT_DATA_FOR_ALL_PILLS_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const exportToExcel = runDeferredRollforwardExport(downloadXlsxBSMReport);
export const exportToCsv = runDeferredRollforwardExport(downloadCsvBSMReport);

export const onAggregationDroplistChange = ({ value }) => ({
  type: CHANGE_AGGREGATION_DROPLIST_VALUE,
  value,
});

export const updateEditedCell = params => {
  const { oldValue: newValue, data } = params;
  const { data: values, key, affectedTotals } = newValue;
  const { value, ...extraValues } = values;

  return {
    type: UPDATE_EDITED_CELL,
    value,
    key,
    description: data.DESCRIPTION,
    extraValues,
    affectedTotals,
  };
};

export const revertEditedCells = () => ({
  type: REVERT_EDITED_CELLS,
});
